/**
 * Warning: this is a PARTIAL copy of "abcstrategie/cogaa" project's file.
 * Each update of the main project should be manually made.
 */

import {utils} from './'
import router from '../router/'
import store from '../store/'
import {Toast} from 'buefy/dist/components/toast'

const auth = store.state.auth
const rolesMapper = {
  superuser: 'superuser',
  direttoregenerale: 'director',
  amministratore: 'admin',
  coordinatore: 'coordinator',
  rup: 'manager',
  caposquadra: 'leader',
  operaio: 'workman'
}

class Api {
  /**
   * Current API version.
   * @returns {string}
   */
  get version () {
    return 'v1'
  }

  /**
   * List of methods which don't require authentication.
   * @returns {string[]}
   */
  get avoidAuthCheckMethods () {
    return [
      'login', // Don't check for access token validity on login action
      'check' // Recursion here, avoid to check
    ]
  }

  /**
   * Update the JWT token in Authorization header.
   * @param {null|string} token
   */
  setAuthorization (token) {
    token = token ? `Bearer ${token}` : token
    window.http.defaults.headers.common['Authorization'] = token
  }

  /**
   * Update the stored token in the localStorage, in the Authorization header and in the IndexedDb.
   * @param {string} token
   * @param timestamp
   */
  setToken (token, timestamp = null) {
    timestamp = timestamp || utils.now()
    // Set token into HTTP requests' headers
    this.setAuthorization(token)
    // Update token into localStorage
    store.dispatch('auth/updateTokenAction', {token, timestamp})
  }

  /**
   * Perform user login action.
   * @param {object} form
   * @returns {Promise<T>}
   */
  login (form) {
    return window.http.post(`/api/${this.version}/users/login`, form)
      .then(response => {
        let data = response.data.data
        let token = data.auth.access_token
        store.dispatch('auth/loginAction', {
          token,
          username: data.user.username,
          name: data.user.name,
          lastname: data.user.lastname,
          role: rolesMapper[data.user.role] || null,
          estate_ids: data.user.estate_ids || null,
          expiration_time: data.auth.expires_in
        })
        this.setToken(token)
      })
  }

  /**
   * Refresh expired access token.
   * @returns {Promise<T>}
   */
  refreshToken () {
    return window.http.put(`/api/${this.version}/users/refresh`)
      .then(response => {
        let error = false
        if (response.status === 200 && typeof response.headers['authorization'] !== 'undefined') {
          let token = response.headers['authorization'].split(' ')[1] || null
          if (token) {
            this.setToken(token)
          } else {
            error = true
          }
        } else {
          error = true
        }
        if (error) {
          store.dispatch('auth/logoutAction')
          router.push({name: 'Login'})
        }
      })
  }

  /**
   * Check for access token validity.
   * @returns {boolean}
   */
  check () {
    if (auth.token) {
      if (auth.expirationDate && utils.now() <= auth.expirationDate) {
        return true
      } else {
        utils.success('Sessione scaduta.')
        store.dispatch('auth/logoutAction')
        router.push('/login')
      }
    }

    return false
  }

  /**
   * Perform a logout action.
   * @returns {Promise<T>}
   */
  logout () {
    return window.http.put(`/api/${this.version}/users/logout`)
      .then(() => {
        store.dispatch('auth/logoutAction')
      })
  }

  /**
   * Get list of estates.
   * @returns {Promise<T>}
   */
  getEstates () {
    return window.http.get(`/api/${this.version}/farms/estates`)
      .then(response => response.data.data)
  }

  getEstate (estateId) {
    return window.http.get(`/api/${this.version}/farms/estates/${estateId}`)
      .then(response => response.data.data)
  }

  /**
   * Get all available members for a team in an estate.
   * @param estateId
   * @return {*|Dexie.Promise<T>|Dexie.Promise<T | any>|Dexie.Promise<any>|Dexie.Promise<any>|PromiseLike<T | never>|Promise<T | never>}
   */
  getAvailableMembers (estateId) {
    return window.http.get(`/api/${this.version}/farms/estates/${estateId}/employees`)
      .then(response => response.data.data)
  }

  /**
   * Get all available leaders
   * @param estateId
   * @returns {PromiseLike<T | never> | Promise<T | never>}
   */
  getAvailableLeaders (estateId) {
    return window.http.get(`/api/${this.version}/farms/estates/${estateId}/available-leaders`)
      .then(response => response.data.data)
  }

  /**
   * Create new employee's signs
   * @param employeeId
   * @param dataURL base64 sign image in format data:image/png,data;.....
   * @returns {Promise<T | never>}
   */
  newSign (employeeId, dataURL) {
    let body = { sign: dataURL }
    return window.http.post(`/api/${this.version}/users/employees/${employeeId}/signs`, body)
      .then(response => response.data.data)
  }

  /**
   * Update user's capability to send statuses
   */
  updateCapability () {
    if (auth.logged && ['workman', 'leader'].indexOf(auth.role) !== -1) {
      window.http.get(`/api/${this.version}/users/current-status`)
        .then(response => {
          let status = response.data.data
          store.dispatch('auth/updateCapabilityAction', status)
        })
    }
  }

  /**
   * Update assigned activities state
   * @return {*|Dexie.Promise<T>|Dexie.Promise<T | any>|Dexie.Promise<any>|Dexie.Promise<any>|PromiseLike<T | never>|Promise<T | never>}
   */
  updateAssignedActivities () {
    return window.http.get(`/api/${this.version}/users/assigned-activities`)
      .then(response => store.dispatch('auth/updateActivitiesAction', response.data.data))
  }

  /**
   * Get products.
   * @return {*|PromiseLike<T | never>|Promise<T | never>}
   */
  getProducts () {
    return window.http.get(`/api/${this.version}/inventories/products`)
      .then(response => response.data.data)
  }

  /**
   * Get warehose of the given estate ID.
   * @param {number} estateId
   * @return {Promise<T | never>}
   */
  getWarehouseByEstate (estateId) {
    return window.http.get(`/api/${this.version}/farms/warehouses/?estates=${estateId}`)
      .then(response => response.data.data[0] || null)
  }

  /**
   * To get the estate warehouse products
   * @param estateId
   * @param cache
   * @returns {PromiseLike<T | never> | Promise<T | never>}
   */
  getWarehousesStockInventory (estateId, cache = false) {
    return window.http.get(`/api/${this.version}/farms/estates/${estateId}/warehouses/stock-inventory`, cache)
      .then(response => response.data.data)
      .catch(err => console.error(err))
  }

  /**
   * Fill warehouse's inventory.
   * @param {number} id
   * @param {Object} items
   * @returns {PromiseLike<T | never> | Promise<T | never>}
   */
  fillWarehouseInventory (id, items) {
    return window.http.put(`/api/${this.version}/farms/warehouses/${id}/inventory`, items)
      .then(response => response.data.data)
  }

  /**
   * Fill warehouse's products inventory.
   * @param {number} id
   * @param {Number} supplierId
   * @param {string} ddtCode
   * @param {string} ddtDate formatted Y-m-d
   * @param {array} products
   * @param {boolean} isPurchaseRequest
   * @returns {PromiseLike<T | never> | Promise<T | never>}
   */
  addProductsToWarehouse (id, supplierId, ddtCode, ddtDate, products, isPurchaseRequest = false) {
    let body = {
      'supplier-id': supplierId,
      'ddt-code': ddtCode,
      'ddt-date': ddtDate,
      products,
      'is-purchase-request': isPurchaseRequest
    }
    return this.fillWarehouseInventory(id, body)
  }

  /**
   * Fill warehouse's tools inventory.
   * @param {number} id
   * @param {array} tools
   * @returns {PromiseLike<T | never> | Promise<T | never>}
   */
  addToolsToWarehouse (id, tools) {
    return this.fillWarehouseInventory(id, {tools})
  }

  /**
   * Fill warehouse's vehicles inventory.
   * @param {number} id
   * @param {array} vehicles
   * @returns {PromiseLike<T | never> | Promise<T | never>}
   */
  addVehiclesToWarehouse (id, vehicles) {
    return this.fillWarehouseInventory(id, {vehicles})
  }

  /**
   * Get a warehouse.
   * @param {number} id
   * @return {Promise<T | never>}
   */
  getWarehouse (id) {
    return window.http.get(`/api/${this.version}/farms/warehouses/${id}`)
      .then(response => response.data.data)
  }

  getPurchaseRequestsProductsByEstate (estateId) {
    return window.http.get(`/api/${this.version}/farms/purchase-requests/products?estate=${estateId}`)
      .then(response => response.data.data)
  }

  /**
   * To get suppliers.
   * @param {number|null} id The supplier identifer
   * @return {PromiseLike<T | never> | Promise<T | never>}
   */
  getSuppliers (id = null) {
    const endpoint = id === null ? `/api/${this.version}/suppliers` : `/api/${this.version}/suppliers/${id}`
    return window.http.get(endpoint)
      .then(response => response.data.data)
  }

  /**
   *  To get order products ordered to the supplier.
   *
   * @param warehouseId
   * @param supplierId
   * @returns {Promise<AxiosResponse<any> | never>}
   */
  getSupplierOrderedProducts (warehouseId, supplierId) {
    return window.http.get(`/api/${this.version}/farms/warehouses/${warehouseId}/suppliers/${supplierId}/orders/products`)
      .then(response => response.data.data)
  }

  /**
   * To load a ddt and its products to the warehouse estate
   * @param {Number} id The warehouse identifier
   * @param {String} ddtCode The ddt-code
   * @param ddtDate The ddt-date
   * @param products The list of products listed in the ddt to be loaded.
   * @returns {Promise<AxiosResponse<any> | never>}
   */
  addOrderedProductsToWarehouse (id, supplierId, ddtCode, ddtDate, products) {
    const ddt = {
      'supplier-id': supplierId,
      'ddt-code': ddtCode,
      'ddt-date': ddtDate,
      products
    }
    return window.http.post(`/api/${this.version}/farms/warehouses/${id}/orders/products`, ddt)
      .then(response => response.data.data)
  }
}

export default new Proxy(new Api(), {
  get: (target, name) => {
    // If the requested attribute exists in the target
    if (name in target) {
      // If the attribute is a function
      if (typeof target[name] === 'function') {
        const method = target[name]
        // Run "check" method before the method call if it isn't one of
        // the methods for which avoid this check
        if (target.avoidAuthCheckMethods.indexOf(name) === -1) {
          return (...args) => {
            // Execute the "check" function
            // Result can be:
            // - true: user is correctly logged in with a valid token
            // - false: not logged in or something wrong
            // - Promise: user is logged but his token is expired,
            //   so wait for refreshing it
            let check = target['check']()
            // If false clear localStorage and login
            if (check === false) {
              store.dispatch('auth/logoutAction')
              router.push('/login')
            } else {
              // If is a Promise, wait for token refreshing, then execute
              // requested method
              if (typeof check.then === 'function') {
                return check
                  .then(() => method.apply(target, args))
                  .catch(e => {
                    // The token has been blacklisted
                    if (e.response.status === 401) {
                      store.dispatch('auth/logoutAction')
                      target.setAuthorization(null)
                      router.push({name: 'Home'})
                      Toast.open({
                        message: 'Sessione scaduta.',
                        type: 'is-danger',
                        position: 'is-bottom'
                      })
                    }
                  })
              } else {
                // Otherwise, execute requested method
                return method.apply(target, args)
              }
            }
          }
        } else {
          // Otherwise, execute requested method without authentication check
          return (...args) => method.apply(target, args)
        }
      }
    }
  }
})



// WEBPACK FOOTER //
// ./src/helpers/api.js